import React, { useState } from 'react';
import { MorganAvatar } from './MorganAvatar';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

export const DatasetSummaryCard = ({ dataset, onAutorun }) => {
  const s = dataset.summary || {};
  const issues = dataset.issue_counts || {};
  const issueTotal = Object.values(issues).reduce((a, b) => a + b, 0);
  return (
    <div className="rounded-lg border bg-sky-50/60 border-sky-200 p-3 text-xs space-y-1.5" data-testid="morgan-dataset-summary">
      <div className="flex items-center justify-between">
        <span className="font-medium text-sm">{dataset.filename}</span>
        <Badge variant="outline" className={!dataset.valid ? 'border-rose-400 text-rose-700' : issueTotal ? 'border-amber-400 text-amber-700' : 'border-emerald-400 text-emerald-700'} data-testid="morgan-dataset-valid">
          {!dataset.valid ? 'Schema issues' : issueTotal ? `Schema valid · ${issueTotal} data issue${issueTotal === 1 ? '' : 's'}` : 'Schema valid'}
        </Badge>
      </div>
      <div data-testid="morgan-dataset-stats">
        Type <b>{dataset.file_type.replace(/_/g, ' ')}</b> · {s.row_count} rows · lane <b>{(s.lane_detected || '').toUpperCase()}</b>
        {s.currency && <> · {s.currency}</>}
        {s.date_range && <> · {s.date_range.from} → {s.date_range.to}</>}
      </div>
      {dataset.missing_required_columns?.length > 0 && (
        <div className="text-rose-700" data-testid="morgan-dataset-missing">Missing required: {dataset.missing_required_columns.join(', ')}</div>
      )}
      {Object.keys(dataset.non_standard_headers || {}).length > 0 && (
        <div data-testid="morgan-dataset-mapping">
          Suggested mapping: {Object.entries(dataset.non_standard_headers).map(([h, m]) => `${h} → ${m.column}`).join(', ')}
        </div>
      )}
      {Object.keys(issues).length > 0 && (
        <div className="text-amber-800" data-testid="morgan-dataset-issues">
          Issues: {Object.entries(issues).map(([k, v]) => `${k.replace(/_/g, ' ')} ${v}`).join(' · ')}
        </div>
      )}
      {dataset.health_score != null && <div data-testid="morgan-dataset-health">Dataset health {dataset.health_score}/100</div>}
      {onAutorun && (
        <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => onAutorun(dataset.id)} data-testid={`morgan-autorun-btn-${dataset.id}`}>
          <MorganAvatar size={16} className="mr-1" /> Run autonomously
        </Button>
      )}
    </div>
  );
};

export const MatchResults = ({ action, canExecute, onOverride, onPrefill, onRevert, onCreateCases }) => {
  const rows = action.output?.results || [];
  if (!rows.length) return null;
  const confident = rows.filter((r) => ['auto', 'override'].includes(r.routing) && (r.matched_mrn || r.override_mrn)).length;
  return (
    <div className="rounded-lg border bg-white p-2 space-y-1 text-xs" data-testid="morgan-match-results">
      {canExecute && onCreateCases && confident > 0 && (
        <div className="flex items-center justify-between gap-2 pb-1 border-b" data-testid="morgan-create-cases-bar">
          <span className="text-slate-600">{confident} of {rows.length} rows can go into UK Import re-export cases</span>
          <Button size="sm" className="h-6 text-[11px]" onClick={() => onCreateCases(action.output.dataset_id)} data-testid="morgan-create-reexport-cases-btn">
            Create UK Import re-export cases
          </Button>
        </div>
      )}
      {rows.slice(0, 15).map((r) => <MatchRow key={r.row_index} r={r} canExecute={canExecute} onOverride={onOverride} onPrefill={onPrefill} onRevert={onRevert} datasetId={action.output.dataset_id} />)}
    </div>
  );
};

const FLAG_LABEL = { data_ghost: 'data ghost (no MRN)', same_state: 'same-state (no export found)', entitlement: 'lane not entitled' };
const TONE = { auto: 'text-emerald-700', review: 'text-amber-700', block: 'text-rose-700', override: 'text-sky-700' };

const MatchRow = ({ r, canExecute, onOverride, onPrefill, onRevert, datasetId }) => {
  const [mrn, setMrn] = useState('');
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b last:border-0 py-1.5" data-testid={`morgan-match-row-${r.row_index}`}>
      <div className="flex justify-between gap-2">
        <span className="font-mono">{r.source_mrn || `row ${r.row_index + 1}`}</span>
        <span className={`font-medium ${TONE[r.routing]}`} data-testid={`morgan-match-routing-${r.row_index}`}>
          {r.routing}{r.routing !== 'override' && ` ${Math.round((r.confidence || 0) * 100)}%`}
        </span>
      </div>
      <div className="text-slate-600">{r.explanation}</div>
      {(r.band || r.workflow_state || r.flags?.length > 0) && (
        <div className="flex flex-wrap gap-1 mt-0.5" data-testid={`morgan-match-tags-${r.row_index}`}>
          {r.band && <Badge variant="outline" className="text-[10px]">{r.band.replace('_', '-')}</Badge>}
          {r.workflow_state && <Badge variant="outline" className="text-[10px]" data-testid={`morgan-match-state-${r.row_index}`}>{r.workflow_state.replace(/_/g, ' ')}</Badge>}
          {(r.flags || []).map((f) => <Badge key={f} variant="outline" className="text-[10px] text-slate-500">{FLAG_LABEL[f] || f.replace(/_/g, ' ')}</Badge>)}
          {r.evidence_score != null && <span className="text-[10px] text-slate-500">evidence {Math.round(r.evidence_score * 100)}%</span>}
        </div>
      )}
      {canExecute && (
        <div className="flex gap-2 mt-1">
          {r.routing === 'auto' && (
            <Button size="sm" variant="outline" className="h-6 text-[11px]" onClick={() => onPrefill(datasetId, r.row_index)} data-testid={`morgan-prefill-${r.row_index}`}>Pre-fill case</Button>
          )}
          {r.routing === 'override' && r.override_id && onRevert && (
            <Button size="sm" variant="outline" className="h-6 text-[11px]" onClick={() => onRevert(r.override_id)} data-testid={`morgan-override-revert-${r.row_index}`}>Revert override</Button>
          )}
          {r.routing === 'block' && !open && (
            <Button size="sm" variant="outline" className="h-6 text-[11px]" onClick={() => setOpen(true)} data-testid={`morgan-override-open-${r.row_index}`}>Provide correct MRN</Button>
          )}
        </div>
      )}
      {open && (
        <div className="flex gap-1 mt-1">
          <Input className="h-7 text-xs font-mono" placeholder="18-char MRN" value={mrn} onChange={(e) => setMrn(e.target.value.toUpperCase())} data-testid={`morgan-override-input-${r.row_index}`} />
          <Button size="sm" className="h-7 text-[11px]" disabled={mrn.length !== 18}
                  onClick={() => { onOverride({ dataset_id: datasetId, row_index: r.row_index, original_mrn: r.source_mrn || null, mrn, reason: 'User-supplied MRN after failed match' }); setOpen(false); }}
                  data-testid={`morgan-override-submit-${r.row_index}`}>Propose</Button>
        </div>
      )}
    </div>
  );
};
